import { configure } from "safe-stable-stringify";
import { create } from "zustand";
import { devtools, persist, type StorageValue } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import type { CallbackState } from "../agent/types";

const safeStringify = configure({
    deterministic: false,
    maximumDepth: 10,
    strict: false,
});
interface CallbackStoreState {
    callbackState: CallbackState | null;
    lastCallbackState: CallbackState | null;
}

export interface CallbackStore extends CallbackStoreState {
    setCallback: (callbackState: Omit<CallbackState, "callbackCount" | "sessionId">) => void;
    clearCallback: () => void;
    setCallbackSuccessful: () => void;
}

export const useCallbackStore = create<CallbackStore>()(
    immer(
        persist(
            devtools(
                (set) => ({
                    callbackState: null,
                    setCallbackSuccessful: () =>
                        set(
                            (state) => {
                                if (!state.callbackState) {
                                    return;
                                }

                                state.lastCallbackState = {
                                    ...state.callbackState,
                                    callbackCount: state.callbackState.callbackCount + 1,
                                };

                                state.callbackState = null;
                            },
                            false,
                            "setCallbackSuccessful",
                        ),
                    setCallback: (callbackState: Omit<CallbackState, "callbackCount" | "sessionId">) =>
                        set(
                            (state) => {
                                const callbackCount =
                                    state.lastCallbackState?.originalTask.id === callbackState.originalTask.id
                                        ? state.lastCallbackState?.callbackCount
                                        : 0;
                                state.callbackState = {
                                    ...callbackState,
                                    callbackCount,
                                    sessionId:
                                        callbackState.originalTask.sessionId ??
                                        callbackState.originalTask.sessionIdFallback,
                                };

                                return state;
                            },
                            false,
                            "setCallback",
                        ),
                    clearCallback: () =>
                        set(
                            (state) => {
                                state.callbackState = null;
                                return state;
                            },
                            false,
                            "clearCallback",
                        ),
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "callback",
                    name: "workspace/sdk",
                },
            ),
            {
                name: "callback-store",
                storage: {
                    getItem(name: string) {
                        const stringifiedState = localStorage.getItem(name);
                        if (!stringifiedState) {
                            return {
                                state: {
                                    callbackState: null,
                                } as CallbackStoreState,
                            } as StorageValue<CallbackStore>;
                        }

                        const parsedCallbackState = JSON.parse(stringifiedState) as CallbackStoreState;

                        return {
                            state: parsedCallbackState,
                        } as StorageValue<CallbackStore>;
                    },
                    setItem(name: string, value: StorageValue<CallbackStore>) {
                        localStorage.setItem(name, safeStringify(value.state));
                    },
                    removeItem: (name: string) => {
                        localStorage.removeItem(name);
                    },
                },
            },
        ),
    ),
);
